import { router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Toaster } from '../../components/ui/sonner'
import { ChevronLeft } from 'lucide-react'

interface VerifyEmailProps {
  provider?: string
  flash?: {
    success?: string
    error?: string
    warning?: string
    info?: string
  }
}

export default function VerifyEmail({ provider, flash }: VerifyEmailProps) {
  const [code, setCode] = useState<string[]>(Array(6).fill(''))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Show flash messages as toasts
  useEffect(() => {
    if (flash?.success) {
      toast.success(flash.success)
    }
    if (flash?.error) {
      toast.error(flash.error)
    }
    if (flash?.warning) {
      toast.warning(flash.warning)
    }
    if (flash?.info) {
      toast.info(flash.info)
    }
  }, [flash])

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1)
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return
    }

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits are filled
    if (index === 5 && value && newCode.every(digit => digit)) {
      handleSubmit(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newCode = [...code]

    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newCode[index] = char
      }
    })

    setCode(newCode)

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => !digit)
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus()
    } else {
      inputRefs.current[5]?.focus()
      // Auto-submit if all digits are filled
      if (newCode.every(digit => digit)) {
        handleSubmit(newCode.join(''))
      }
    }
  }

  const handleSubmit = (codeValue: string) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    router.post('/auth/provider/verify-email', { code: codeValue }, {
      onFinish: () => setIsSubmitting(false)
    })
  }

  const providerName = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'the provider'

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <Toaster />

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold mb-4">Verify your email</h1>
          <p className="text-muted-foreground">
            Enter the code sent to your email to complete signing in with {providerName}
          </p>
        </div>

        <div className="bg-card border rounded-2xl p-8 mb-6">
          {/* 6-digit code inputs */}
          <div className="flex justify-center gap-2 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isSubmitting}
                className="w-14 h-14 text-center text-2xl font-semibold border-2 rounded-xl
                         focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all"
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>

          {/* Resend link */}
          <div className="text-center text-sm text-muted-foreground">
            Didn't receive a code?{' '}
            <button
              type="button"
              onClick={() => window.location.href = '/login'}
              className="text-foreground hover:underline font-medium"
            >
              Back to sign-in
            </button>
          </div>
        </div>

        {/* Back button */}
        <button
          type="button"
          onClick={() => window.location.href = '/login'}
          className="flex items-center justify-center w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to sign-in
        </button>
      </div>
    </div>
  )
}
